// สรุปผู้เข้าสอบ — รายวัน และรวมทั้งช่วงสอบ แยกภาค: สามัญ (วัดผล 10/11, วิชาการ 3/4)
// และศาสนา (วัดผลศาสนา 52, หัวหน้าวิชาการศาสนา 57) — แต่ละฝ่ายเห็นเฉพาะช่วงสอบของภาคตัวเอง
//
// หน่วยนับคือ "คน-วิชา" — วันหนึ่งสอบหลายวิชา ห้อง 30 คนสอบ 3 วิชา = 90 ที่นั่งสอบ
// ผู้เข้าสอบ = มาสอบ + มาสาย ต่อที่นั่งสอบของวิชาที่ครูคุมสอบเช็คแล้วเท่านั้น
// ระบบไม่มีตารางสอบ จึงไม่รู้ว่าวันนั้นควรมีกี่วิชา — แสดงจำนวนวิชา-ห้องที่เช็คแล้ว และห้องที่
// ยังไม่มีการเช็คเลยคู่กันเสมอ ไม่งั้นวันที่เช็คไปห้องเดียวแล้วมาครบจะขึ้น 100%
//
// หน้านี้ไม่ล็อกหลังช่วงสอบ เพราะเป็นข้อมูลของฝ่าย
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Card, Col, Form, ProgressBar, Row, Spinner, Table } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import axiosInstance from '../services/axiosInterceptor';
import { toISODate } from '../utils/thaiDate';
import { API, apiError, attendPct, dayLabel, eventName, eventRange, fmtPct, pickEvent, useExamEvents } from './examAbsenceUtils';

const pctVariant = (v) => (v == null ? 'secondary' : v >= 95 ? 'success' : v >= 85 ? 'warning' : 'danger');

const PctCell = ({ row }) => {
  const v = attendPct(row);
  return <span className={`fw-semibold text-${pctVariant(v)}`}>{fmtPct(v)}</span>;
};

const RoomsCell = ({ row }) => (
  <span className={row.rooms_checked < row.rooms_total ? 'text-warning' : 'text-secondary'}>
    {row.rooms_checked}/{row.rooms_total}
  </span>
);

const CountCells = ({ row, absentLink }) => (
  <>
    <td className="text-end">{row.sittings || '–'}</td>
    <td className="text-end">{row.total.toLocaleString()}</td>
    <td className="text-end">{(row.present + row.late).toLocaleString()}</td>
    <td className="text-end">{row.late || '–'}</td>
    <td className="text-end">{row.on_leave || '–'}</td>
    <td className="text-end">
      {row.absent ? (absentLink ? <Link to={absentLink} className="text-danger fw-semibold">{row.absent}</Link> : row.absent) : '–'}
    </td>
    <td className="text-end">{row.absent_students || '–'}</td>
  </>
);

// รายวิชา — วิชานั้นขาดกี่คนในแต่ละห้อง ห้องที่ไม่มีคนขาดไม่ขึ้นเป็นป้าย (นับรวมเป็น "ครบ n ห้อง")
const SubjectCard = ({ data, day, onDay }) => {
  const subjects = (data.subjects || []).filter((s) => !day || s.dates.includes(day));
  return (
    <Card className="card-one mb-3">
      <Card.Header className="d-flex flex-wrap align-items-center gap-2">
        <Card.Title as="h6" className="mb-0">ขาดสอบรายวิชา</Card.Title>
        <Form.Select size="sm" className="ms-auto" style={{ maxWidth: 220 }} value={day} onChange={(e) => onDay(e.target.value)}>
          <option value="">ทุกวัน</option>
          {data.days.map((d, i) => <option key={d.date} value={d.date}>วันที่ {i + 1} · {dayLabel(d.date)}</option>)}
        </Form.Select>
      </Card.Header>
      <Card.Body className="p-0">
        <Table responsive hover className="mb-0 align-middle">
          <thead>
            <tr>
              <th>วิชา</th>
              <th className="text-center">ห้องที่เช็ค</th>
              <th className="text-end">ขาด</th>
              <th className="text-end">ลา</th>
              <th className="text-end">สาย</th>
              <th className="text-end">% เข้าสอบ</th>
              <th>ขาดรายห้อง</th>
            </tr>
          </thead>
          <tbody>
            {subjects.length === 0 ? (
              <tr><td colSpan={7} className="text-center text-secondary py-4">ยังไม่มีการเช็ค{day ? 'ในวันนี้' : ''}</td></tr>
            ) : subjects.map((s) => {
              const absentRooms = s.rooms.filter((r) => r.absent > 0);
              const fullRooms = s.rooms.length - absentRooms.length;
              return (
                <tr key={s.key}>
                  <td>
                    {s.label}
                    {!day && <small className="d-block text-secondary">{s.dates.map((d) => dayLabel(d)).join(', ')}</small>}
                  </td>
                  <td className="text-center text-secondary">{s.rooms.length}</td>
                  <td className="text-end">{s.absent ? <span className="text-danger fw-semibold">{s.absent}</span> : '–'}</td>
                  <td className="text-end">{s.on_leave || '–'}</td>
                  <td className="text-end">{s.late || '–'}</td>
                  <td className="text-end"><PctCell row={s} /></td>
                  <td>
                    <div className="d-flex flex-wrap gap-1">
                      {absentRooms.map((r) => (
                        <Badge key={r.r_id} bg="danger" className="fw-normal" title={`ลา ${r.on_leave} · สาย ${r.late} · ทั้งห้อง ${r.total} คน`}>
                          {r.label} · {r.absent}
                        </Badge>
                      ))}
                      {fullRooms > 0 && <span className="fs-xs text-secondary align-self-center">ไม่มีคนขาด {fullRooms} ห้อง</span>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

export default function ExamAbsenceSummary() {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];
  const [params, setParams] = useSearchParams();
  const { loading: evLoading, events, error: evError } = useExamEvents();
  const today = toISODate(new Date());
  // หน้าสรุปเป็นของเจ้าของข้อมูล — สามัญ: วัดผล/วิชาการ, ศาสนา: วัดผลศาสนา/หัวหน้าวิชาการศาสนา
  const manageable = useMemo(() => events.filter((e) => e.canManage), [events]);
  const ev = useMemo(() => pickEvent(manageable, params.get('cal'), today), [manageable, params, today]);

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [subjectDay, setSubjectDay] = useState('');

  useEffect(() => {
    if (!ev) return undefined;
    let alive = true;
    setData(null);
    setError(null);
    axiosInstance.get(`${API}/exam-absence/summary`, { params: { cal_id: ev.cal_id } })
      .then(({ data: res }) => alive && setData(res))
      .catch((err) => alive && setError(apiError(err)));
    return () => { alive = false; };
  }, [ev]);

  const absentLink = (date) => `/exam-absence/absent?cal=${ev.cal_id}${date ? `&date=${date}` : '&date=all'}`;
  const overallPct = data ? attendPct(data.overall) : null;

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-2 p-lg-4">
        <div className="d-flex flex-wrap align-items-end justify-content-between gap-2 mb-3">
          <div>
            <h4 className="main-title mb-0">สรุปผู้เข้าสอบ</h4>
            <div className="fs-xs text-secondary">นับเป็นคน-วิชา · ผู้เข้าสอบ = มาสอบ + มาสาย · คิดจากวิชาที่ครูคุมสอบเช็คแล้วเท่านั้น</div>
          </div>
          {manageable.length > 0 && ev && (
            <Form.Select size="sm" style={{ maxWidth: 380 }} value={ev.cal_id} onChange={(e) => setParams({ cal: e.target.value })}>
              {manageable.map((e) => <option key={e.cal_id} value={e.cal_id}>{eventName(e)} ({eventRange(e)})</option>)}
            </Form.Select>
          )}
        </div>

        {evLoading && <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>}
        {evError && <Alert variant="danger">{evError}</Alert>}
        {!evLoading && !evError && !ev && (
          <Alert variant="secondary">ยังไม่มีช่วงสอบของฝ่ายคุณในปฏิทิน — เพิ่มรายการประเภท "สอบสามัญ" หรือ "สอบศาสนา" ที่ปฏิทินปฏิบัติงาน</Alert>
        )}
        {error && <Alert variant="danger">{error}</Alert>}
        {ev && !data && !error && <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>}

        {data && (
          <>
            <Row className="g-3 mb-3">
              <Col md={4}>
                <Card className="card-one h-100">
                  <Card.Body>
                    <div className="text-secondary fs-sm">ผู้เข้าสอบรวมทั้ง {data.days.length} วัน</div>
                    <div className={`display-6 fw-bold text-${pctVariant(overallPct)}`}>{fmtPct(overallPct)}</div>
                    <ProgressBar now={overallPct || 0} variant={pctVariant(overallPct)} className="my-2" style={{ height: 6 }} />
                    <div className="fs-xs text-secondary">
                      ขาดสอบ <Link to={absentLink(null)} className="text-danger fw-semibold">{data.overall.absent}</Link> คน-วิชา
                      ({data.overall.absent_students} คน) · ลา {data.overall.on_leave} · สาย {data.overall.late}
                      · เช็คแล้ว {data.overall.sittings} วิชา-ห้อง
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              {data.days.map((d, i) => {
                const v = attendPct(d);
                return (
                  <Col key={d.date} xs={6} md>
                    <Card className={`card-one h-100 ${d.date === data.today ? 'border-primary' : ''}`}>
                      <Card.Body>
                        <div className="text-secondary fs-sm">
                          วันที่ {i + 1} · {dayLabel(d.date)} {d.date === data.today && <Badge bg="primary">วันนี้</Badge>}
                        </div>
                        <div className={`h3 fw-bold mb-1 text-${pctVariant(v)}`}>{fmtPct(v)}</div>
                        <div className="fs-xs text-secondary">
                          {d.sittings ? <>ขาด <Link to={absentLink(d.date)} className="text-danger">{d.absent}</Link> · {d.sittings} วิชา-ห้อง · </> : null}
                          ห้องที่เช็คแล้ว {d.rooms_checked}/{d.rooms_total}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>

            <Card className="card-one mb-3">
              <Card.Header><Card.Title as="h6" className="mb-0">รายวัน</Card.Title></Card.Header>
              <Card.Body className="p-0">
                <Table responsive hover className="mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>วันสอบ</th>
                      <th className="text-center">ห้องที่เช็ค</th>
                      <th className="text-end">วิชา-ห้อง</th>
                      <th className="text-end">ที่นั่งสอบ</th>
                      <th className="text-end">เข้าสอบ</th>
                      <th className="text-end">สาย</th>
                      <th className="text-end">ลา</th>
                      <th className="text-end">ขาด (คน-วิชา)</th>
                      <th className="text-end">ขาด (คน)</th>
                      <th className="text-end">% เข้าสอบ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.days.map((d, i) => (
                      <tr key={d.date}>
                        <td>วันที่ {i + 1} · {dayLabel(d.date)}</td>
                        <td className="text-center"><RoomsCell row={d} /></td>
                        <CountCells row={d} absentLink={absentLink(d.date)} />
                        <td className="text-end"><PctCell row={d} /></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="fw-semibold">
                    <tr>
                      <td>รวมทั้ง {data.days.length} วัน</td>
                      <td className="text-center text-secondary">–</td>
                      <CountCells row={data.overall} absentLink={absentLink(null)} />
                      <td className="text-end"><PctCell row={data.overall} /></td>
                    </tr>
                  </tfoot>
                </Table>
              </Card.Body>
            </Card>

            <SubjectCard data={data} day={subjectDay} onDay={setSubjectDay} />

            <Card className="card-one">
              <Card.Header><Card.Title as="h6" className="mb-0">% เข้าสอบรายระดับชั้น</Card.Title></Card.Header>
              <Card.Body className="p-0">
                <Table responsive hover className="mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>ระดับชั้น</th>
                      {data.days.map((d, i) => <th key={d.date} className="text-end">วันที่ {i + 1}<div className="fs-xs fw-normal text-secondary">{dayLabel(d.date)}</div></th>)}
                      <th className="text-end">รวม</th>
                      <th className="text-end">ขาด (คน-วิชา)</th>
                      <th className="text-end">ขาด (คน)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.overall.classes.map((c) => (
                      <tr key={c.c_id}>
                        <td>{c.label}</td>
                        {data.days.map((d) => {
                          const row = d.classes.find((x) => x.c_id === c.c_id);
                          return (
                            <td key={d.date} className="text-end">
                              <PctCell row={row} />
                              {row.sittings > 0 && (
                                <div className={`fs-xs ${row.rooms_checked < row.rooms_total ? 'text-warning' : 'text-secondary'}`}>
                                  {row.sittings} วิชา-ห้อง · {row.rooms_checked}/{row.rooms_total} ห้อง
                                </div>
                              )}
                            </td>
                          );
                        })}
                        <td className="text-end"><PctCell row={c} /></td>
                        <td className="text-end">{c.absent || '–'}</td>
                        <td className="text-end">{c.absent_students || '–'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </>
        )}

        <Footer />
      </div>
    </>
  );
}
